/**
 * This component will show the booking info and calculated total price.
 * I.e. dates and other details related to payment decision in receipt format.
 */
import React from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  DATE_TYPE_DATE,
  DATE_TYPE_DATETIME,
  DATE_TYPE_TIME,
  LINE_ITEM_CUSTOMER_COMMISSION,
  LINE_ITEM_FIXED,
  LINE_ITEM_HOUR,
  LINE_ITEM_PROVIDER_COMMISSION,
  LISTING_UNIT_TYPES,
  propTypes,
} from '../../util/types';

import LineItemBookingPeriod from './LineItemBookingPeriod';
import LineItemBasePriceMaybe from './LineItemBasePriceMaybe';
import LineItemSubTotalMaybe from './LineItemSubTotalMaybe';
import LineItemShippingFeeMaybe from './LineItemShippingFeeMaybe';
// CUSTOM-START: shipping-checkout
import LineItemShippingPendingMaybe from './LineItemShippingPendingMaybe';
// CUSTOM-END: shipping-checkout
import LineItemPickupFeeMaybe from './LineItemPickupFeeMaybe';
import LineItemCustomerCommissionMaybe from './LineItemCustomerCommissionMaybe';
import LineItemCustomerCommissionRefundMaybe from './LineItemCustomerCommissionRefundMaybe';
import LineItemProviderCommissionMaybe from './LineItemProviderCommissionMaybe';
import LineItemProviderCommissionRefundMaybe from './LineItemProviderCommissionRefundMaybe';
import LineItemRefundMaybe from './LineItemRefundMaybe';
import LineItemTotalPrice from './LineItemTotalPrice';
import LineItemUnknownItemsMaybe from './LineItemUnknownItemsMaybe';
// CUSTOM-START: cl-cart-01
import { LINE_ITEM_CART_ITEM } from '../../util/types';
import LineItemCartItemsMaybe from './LineItemCartItemsMaybe';
// CUSTOM-END: cl-cart-01
// CUSTOM-START: checkout-money-rows
import { LINE_ITEM_ITEM, LINE_ITEM_SHIPPING_FEE } from '../../util/types';
import LineItemCheckoutItemSubtotal from './LineItemCheckoutItemSubtotal';
import LineItemCheckoutTotal from './LineItemCheckoutTotal';
// CUSTOM-END: checkout-money-rows
// CUSTOM-START: cl-neg-01
import LineItemOfferAdjustmentMaybe from './LineItemOfferAdjustmentMaybe';
// CUSTOM-END: cl-neg-01
// CUSTOM-START: cl-disc-01
import LineItemDiscountMaybe from './LineItemDiscountMaybe';
import LineItemRevisedItemTotalMaybe from './LineItemRevisedItemTotalMaybe';
import LineItemOfferSavingsMaybe from './LineItemOfferSavingsMaybe';
// CUSTOM-END: cl-disc-01

import css from './OrderBreakdown.module.css';

export const OrderBreakdownComponent = props => {
  const {
    rootClassName,
    className,
    userRole,
    transaction,
    booking,
    timeZone,
    currency,
    marketplaceName,
    // CUSTOM-START: shipping-checkout
    deliveryMethod,
    // CUSTOM-END: shipping-checkout
    // CUSTOM-START: checkout-money-rows
    // 'checkout' | 'order' | undefined. Set only by pages that list the items separately above
    // the money rows, so the per-item rows would otherwise be shown twice. Undefined everywhere
    // else — the listing-page estimate and any page that has no item list keep the template's
    // rows exactly as they were.
    summaryVariant,
    // CUSTOM-END: checkout-money-rows
    intl,
  } = props;

  const isCustomer = userRole === 'customer';
  const isProvider = userRole === 'provider';
  const allLineItems = transaction.attributes.lineItems || [];
  // CUSTOM-START: cl-cart-01
  const isBasket = allLineItems.some(li => li.code === LINE_ITEM_CART_ITEM && !li.reversal);
  // CUSTOM-END: cl-cart-01
  // We'll show only line-items that are specific for the current userRole (customer vs provider)
  const lineItems = allLineItems.filter(lineItem => lineItem.includeFor.includes(userRole));
  const unitLineItem = lineItems.find(
    item => LISTING_UNIT_TYPES.includes(item.code) && !item.reversal
  );
  // Line-item code that matches with base unit: day, night, hour, fixed, item
  const lineItemUnitType = unitLineItem?.code;
  // CUSTOM-START: checkout-money-rows
  // The checkout panel's money rows are worded as CartCheckoutPage words them, so the two
  // checkouts read alike. Both swaps are opt-in and customer-only:
  //
  //   - the subtotal swap also requires an item-based unit type. A booking's base row reads
  //     "$450.00 x 3 nights", which carries the nights; collapsing that to "Item subtotal" would
  //     drop information no other row on a booking checkout shows.
  //   - the total swap applies to every process. "Total price" is simply wrong while shipping is
  //     unpriced, whatever is being bought.
  // The subtotal swap applies to BOTH roles: the seller's panel lists the items above these rows
  // too, so leaving the per-item lines in would say it twice on the sale page as well.
  const useCheckoutItemSubtotal =
    !!summaryVariant && (isBasket || lineItemUnitType === LINE_ITEM_ITEM);
  // The total swap is customer-only. A provider's total is "You'll earn" / "You earned" /
  // "Total price" depending on whether the order completed or was refunded — none of which
  // LineItemCheckoutTotal knows, and all of which LineItemTotalPrice already gets right.
  const useCheckoutTotal = !!summaryVariant && isCustomer;
  const shippingPending =
    deliveryMethod === 'shipping' &&
    !lineItems.some(li => li.code === LINE_ITEM_SHIPPING_FEE && !li.reversal);
  // CUSTOM-END: checkout-money-rows
  const dateType = [LINE_ITEM_HOUR, LINE_ITEM_FIXED].includes(lineItemUnitType)
    ? DATE_TYPE_DATETIME
    : DATE_TYPE_DATE;

  const hasCommissionLineItem = lineItems.find(item => {
    const hasCustomerCommission = isCustomer && item.code === LINE_ITEM_CUSTOMER_COMMISSION;
    const hasProviderCommission = isProvider && item.code === LINE_ITEM_PROVIDER_COMMISSION;
    return (hasCustomerCommission || hasProviderCommission) && !item.reversal;
  });

  const classes = classNames(rootClassName || css.root, className);

  /**
   * OrderBreakdown contains different line items:
   *
   * LineItemBookingPeriod: prints booking start and booking end types. Prop dateType
   * determines if the date and time or only the date is shown
   *
   * LineItemShippingFeeMaybe: prints the shipping fee (combining additional fee of
   * additional items into it).
   *
   * LineItemShippingFeeRefundMaybe: prints the amount of refunded shipping fee
   *
   * LineItemBasePriceMaybe: prints the base price calculation for the listing, e.g.
   * "$150.00 * 2 nights $300"
   *
   * LineItemUnknownItemsMaybe: prints the line items that are unknown. In ideal case there
   * should not be unknown line items. If you are using custom pricing, you should create
   * new custom line items if you need them.
   *
   * LineItemSubTotalMaybe: prints subtotal of line items before possible
   * commission or refunds
   *
   * LineItemRefundMaybe: prints the amount of refund
   *
   * LineItemCustomerCommissionMaybe: prints the amount of customer commission
   * The default transaction process used by this template doesn't include the customer commission.
   *
   * LineItemCustomerCommissionRefundMaybe: prints the amount of refunded customer commission
   *
   * LineItemProviderCommissionMaybe: prints the amount of provider commission
   *
   * LineItemProviderCommissionRefundMaybe: prints the amount of refunded provider commission
   *
   * LineItemTotalPrice: prints total price of the transaction
   *
   */

  return (
    <div className={classes}>
      <LineItemBookingPeriod
        booking={booking}
        code={lineItemUnitType}
        dateType={dateType}
        timeZone={timeZone}
      />

      {/* CUSTOM-START: cl-cart-01 */}
      {/* A basket lists every item, anchor included, so LineItemBasePriceMaybe stands down —
          it labels the anchor "Unit price", which reads wrong beside rows carrying titles.
          `isBasket` is false for every single-item order and every pre-Milestone-B order, so
          both render exactly as they always did. */}
      {isBasket || useCheckoutItemSubtotal ? null : (
        <LineItemBasePriceMaybe lineItems={lineItems} code={lineItemUnitType} intl={intl} />
      )}
      {useCheckoutItemSubtotal ? (
        <LineItemCheckoutItemSubtotal lineItems={lineItems} intl={intl} />
      ) : null}
      {/* Stands down for the same reason: when the panel lists every item above these rows,
          repeating them here itemises the order twice in one card. */}
      {useCheckoutItemSubtotal ? null : (
        <LineItemCartItemsMaybe
          lineItems={lineItems}
          cart={transaction?.attributes?.protectedData?.cart}
        />
      )}
      {/* CUSTOM-END: cl-cart-01 */}
      {/* CUSTOM-START: cl-neg-01 */}
      {/* Requirement 3's row order: items (above) → what was negotiated off → what was
          discounted → the revised total → shipping (below). */}
      <LineItemOfferAdjustmentMaybe lineItems={lineItems} intl={intl} />
      {/* CUSTOM-END: cl-neg-01 */}
      {/* CUSTOM-START: cl-disc-01 */}
      <LineItemDiscountMaybe lineItems={lineItems} intl={intl} />
      <LineItemRevisedItemTotalMaybe
        negotiation={transaction?.attributes?.protectedData?.negotiation}
        currency={currency}
        intl={intl}
      />
      <LineItemOfferSavingsMaybe
        negotiation={transaction?.attributes?.protectedData?.negotiation}
        isCustomer={isCustomer}
        currency={currency}
        intl={intl}
      />
      {/* CUSTOM-END: cl-disc-01 */}
      <LineItemShippingFeeMaybe lineItems={lineItems} intl={intl} />
      {/* CUSTOM-START: shipping-checkout */}
      <LineItemShippingPendingMaybe lineItems={lineItems} deliveryMethod={deliveryMethod} />
      {/* CUSTOM-END: shipping-checkout */}
      <LineItemPickupFeeMaybe lineItems={lineItems} intl={intl} />
      <LineItemUnknownItemsMaybe lineItems={lineItems} isProvider={isProvider} intl={intl} />

      <LineItemSubTotalMaybe
        lineItems={lineItems}
        code={lineItemUnitType}
        userRole={userRole}
        intl={intl}
        marketplaceCurrency={currency}
      />
      <LineItemRefundMaybe lineItems={lineItems} intl={intl} marketplaceCurrency={currency} />

      <LineItemCustomerCommissionMaybe
        lineItems={lineItems}
        isCustomer={isCustomer}
        marketplaceName={marketplaceName}
        intl={intl}
      />
      <LineItemCustomerCommissionRefundMaybe
        lineItems={lineItems}
        isCustomer={isCustomer}
        marketplaceName={marketplaceName}
        intl={intl}
      />

      <LineItemProviderCommissionMaybe
        lineItems={lineItems}
        isProvider={isProvider}
        marketplaceName={marketplaceName}
        intl={intl}
      />
      <LineItemProviderCommissionRefundMaybe
        lineItems={lineItems}
        isProvider={isProvider}
        marketplaceName={marketplaceName}
        intl={intl}
      />

      {/* CUSTOM-START: checkout-money-rows */}
      {useCheckoutTotal ? (
        <LineItemCheckoutTotal
          transaction={transaction}
          shippingPending={shippingPending}
          variant={summaryVariant}
          intl={intl}
        />
      ) : (
        <LineItemTotalPrice transaction={transaction} isProvider={isProvider} intl={intl} />
      )}
      {/* CUSTOM-END: checkout-money-rows */}

      {hasCommissionLineItem ? (
        <span className={css.feeInfo}>
          <FormattedMessage id="OrderBreakdown.commissionFeeNote" />
        </span>
      ) : null}
    </div>
  );
};

/**
 * Order breakdown aka receipt
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string} props.marketplaceName
 * @param {string} [props.deliveryMethod] CUSTOM shipping-checkout: 'shipping' | 'pickup'
 * @param {string} props.timeZone IANA time zone name
 * @param {string} props.currency E.g. 'USD'
 * @param {'customer' | 'provider'} props.userRole
 * @param {propTypes.transaction} props.transaction
 * @param {propTypes.booking?} props.booking
 * @param {DATE_TYPE_DATE | DATE_TYPE_TIME | DATE_TYPE_DATETIME} props.dateType
 * @returns {JSX.Element} the order breakdown component
 */
const OrderBreakdown = props => {
  const intl = useIntl();
  return <OrderBreakdownComponent intl={intl} {...props} />;
};

export default OrderBreakdown;
